import { useEffect, useMemo, useState } from 'react'

import {
  Box,
  Title,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'
import * as Sentry from '@sentry/browser'
import { isEqual, reduce } from 'lodash'

import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { DATEV_EPHEMERAL_KEY_PATTERNS } from 'modules/payroll_integrations_datev_lauds/lib/ephemeralKeys'
import { DatevWizardData } from 'modules/payroll_integrations_datev_lauds/lib/types'

import { LegalEntityCard } from './LegalEntityCard'

const CompanyStep = ({ data, onDataChanged }: WizardStepRenderParams<DatevWizardData>) => {
  const [errorStates, setErrorStates] = useState<Set<string>>(new Set())
  const [consultantFilled, setConsultantFilled] = useState(true)
  const [dataCopied, setDataCopied] = useState(false)
  const [payrollSoftwareFilled, setPayrollSoftwareFilled] = useState(true)

  const {
    access: { id: accessId },
    company: { id: companyId, legalEntities },
  } = useCurrentSession()

  useEffect(() => {
    setErrorStates(new Set())
    setConsultantFilled(true)
    setPayrollSoftwareFilled(true)
  }, [data.answers.consultant_id, data.answers.payroll_software])

  const storedData = localStorage.getItem('datev_lauds_form_data')
  // Memoised to avoid re-parsing on every render; also strips stale auth keys.
  const parsedData = useMemo(() => {
    try {
      const raw = JSON.parse(storedData || '{}')
      if (!raw.answers) return raw
      return {
        ...raw,
        answers: Object.fromEntries(
          Object.entries(raw.answers).filter(
            ([key]) => !DATEV_EPHEMERAL_KEY_PATTERNS.some((pattern) => pattern.test(key))
          )
        ),
      }
    } catch {
      return {}
    }
  }, [storedData])

  useEffect(() => {
    // Try to retrieve stored form data
    if (storedData) {
      try {
        if (parsedData.answers) {
          const parsedDataFilledFields = Object.keys(parsedData.answers).filter(
            (key: string) => parsedData.answers[key] !== ''
          ).length
          const dataFilledFields = Object.keys(data.answers).filter(
            (key: string) => data.answers[key] !== ''
          ).length

          const changedValues = reduce(
            parsedData.answers,
            function (result: string[], value, key) {
              return isEqual(value, data.answers[key]) ? result : result.concat(key)
            },
            []
          )

          if (
            changedValues.length > 0 &&
            dataFilledFields < parsedDataFilledFields &&
            !dataCopied
          ) {
            const baseAnswers = Object.fromEntries(
              Object.entries(data.answers).filter(
                ([key]) => !DATEV_EPHEMERAL_KEY_PATTERNS.some((pattern) => pattern.test(key))
              )
            )
            onDataChanged({
              ...data,
              answers: {
                ...baseAnswers,
                ...parsedData.answers,
              },
            })
          }
        }
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            companyId: companyId,
            flow: '[DATEV LAUDS Company Step]',
          },
        })
      } finally {
        if (parsedData.answers) {
          const parsedDataFilledFields = Object.keys(parsedData.answers).filter(
            (key: string) => parsedData.answers[key] !== ''
          ).length
          const dataFilledFields = Object.keys(data.answers).filter(
            (key: string) => data.answers[key] !== ''
          ).length

          if (dataFilledFields >= parsedDataFilledFields) {
            setDataCopied(true)
          }
        }
      }
    }
  }, [data])

  useOnNext(() => {
    const legalEntitiesKeys = Object.keys(data.answers).filter((key) =>
      key.startsWith('legal_entity_')
    )

    const mappedLegalEntityCount = legalEntitiesKeys.map((key) => !!data.answers[key]).length
    if (data.answers.consultant_id && mappedLegalEntityCount > 0) {
      ;(track(trackingEvents.payrollIntegrationsDatevLauds.datevLaudsSettingsConsultantIDSelected),
        {
          company_id: companyId,
          access_id: accessId,
        })

      ;(track(trackingEvents.payrollIntegrationsDatevLauds.datevLaudsSettingsClientIdMapped),
        {
          company_id: companyId,
          access_id: accessId,
          legal_entities_mapped: mappedLegalEntityCount,
        })
    }
  })

  const handleError = (legalEntityId: string, hasError: boolean) => {
    setErrorStates((prev) => {
      const newStates = new Set(prev)
      if (hasError) {
        newStates.add(legalEntityId)
      } else {
        newStates.delete(legalEntityId)
      }
      return newStates
    })
  }

  useEffect(() => {
    const hasErrors = errorStates.size > 0
    if (data.answers['hasErrors'] !== hasErrors.toString()) {
      onDataChanged({
        ...data,
        answers: {
          ...data.answers,
          hasErrors: hasErrors.toString(),
        },
      })
    }
  }, [errorStates, data, onDataChanged])

  const softwareOptions: SelectOption[] = [
    {
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.payroll_software.options.datev_lodas'
      ),
      value: 'datev_lodas',
    },
    {
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.payroll_software.options.datev_lug'
      ),
      value: 'datev_lug',
    },
  ]

  const fieldsRequiredError = i18n.t(
    'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.login.offline_access_token.fields_required'
  )

  return (
    <>
      <Box flexDirection="row" gap="s96">
        <Box gap="s24">
          <Title
            title={i18n.t(
              'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.company.title'
            )}
            description={i18n.t(
              'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.company.description'
            )}
          />

          <Box justifyContent="flexStart" flexDirection="column" gap="s32">
            <Box justifyContent="flexStart" flexDirection="column" gap="s32">
              <TextField
                label={i18n.t(
                  'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.company.consultant_text_label'
                )}
                value={data.answers.consultant_id}
                onChange={(value) => {
                  onDataChanged({
                    ...data,
                    answers: {
                      ...data.answers,
                      consultant_id: value.toString(),
                    },
                  })
                }}
                placeholder="1234567"
                maxLength={7}
                error={!consultantFilled ? fieldsRequiredError : undefined}
              />
              <SelectField
                label={i18n.t(
                  'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.company.payroll_software_label'
                )}
                options={softwareOptions}
                none={i18n.t(
                  'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.payroll_software.options.none'
                )}
                value={data.answers.payroll_software}
                onChange={(value) => {
                  onDataChanged({
                    ...data,
                    answers: {
                      ...data.answers,
                      payroll_software: value,
                    },
                  })
                }}
                error={!payrollSoftwareFilled ? fieldsRequiredError : undefined}
              />
              {legalEntities.map((legalEntity) => (
                <>
                  <LegalEntityCard
                    key={legalEntity.id}
                    data={data}
                    legalEntity={legalEntity}
                    completed
                    loading={false}
                    onDataChanged={onDataChanged}
                    handleError={handleError}
                    setConsultantFilled={setConsultantFilled}
                    setPayrollSoftwareFilled={setPayrollSoftwareFilled}
                  />
                </>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  )
}

export const companyStep = (): WizardStep<DatevWizardData> => ({
  title: i18n.t(
    'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.company.wizard_title'
  ),
  nextText: i18n.t('common.next'),
  render: (props) => <CompanyStep {...props} />,
  isCompleted: (data: DatevWizardData) => {
    const consultantIdFilled = !!data.answers.consultant_id

    const legalEntityKeys = Object.keys(data.answers).filter((key) =>
      DATEV_EPHEMERAL_KEY_PATTERNS.some((pattern) => pattern.test(key))
    )

    const hasAtLeastOneLegalEntity =
      legalEntityKeys.length > 0 && legalEntityKeys.some((key) => !!data.answers[key])
    const payrollSoftwareFilled = !!data.answers.payroll_software

    const hasErrors = data.answers.hasErrors === 'true'

    return hasAtLeastOneLegalEntity && consultantIdFilled && payrollSoftwareFilled && !hasErrors
  },
})
